import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import LogoFallback from "/assets/img/bubbanlogo.jpg";
import axios from "axios";

import { motion, AnimatePresence } from "framer-motion";

import {
  Search,
  CalendarDays,
  ArrowRight,
  Newspaper,
  Sparkles,
  Clock,
  X,
  RefreshCw,
  ChevronRight,
  TrendingUp,
  Image as ImageIcon,
} from "lucide-react";

export default function NewsList({
  logoSrc = LogoFallback,
  accent = "#115e32",
  pageTitle = "News | Bandarban University",
}) {
  const [news, setNews] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // =========================================
  // FETCH NEWS
  // =========================================

  const newsPost = async () => {
    setLoading(true);
    setError(null);

    try {
      const payload = {
        userId: import.meta.env.VITE_API_USER,
        password: import.meta.env.VITE_API_PASS,
      };

      const api = axios.create({
        baseURL: import.meta.env.VITE_API_BASE_URL,
        headers: {
          "Content-Type": "application/json",
        },
      });

      const response = await api.post("/all-news", payload);

      const data = Array.isArray(response.data)
        ? response.data
        : [];

      setNews(data);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Something went wrong"
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================
  // INITIAL LOAD
  // =========================================

  useEffect(() => {
    newsPost();
  }, []);

  // =========================================
  // BROWSER TITLE
  // =========================================

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  // =========================================
  // FILTER NEWS
  // =========================================

  const filteredNews = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    if (!keyword) {
      return news.slice(0, 50);
    }

    return news.filter((item) =>
      String(item.head_line || "")
        .toLowerCase()
        .includes(keyword)
    );
  }, [news, search]);

  // =========================================
  // DATE FORMAT
  // =========================================

  const formatDate = (dateStr) => {
    if (!dateStr) return "Date unavailable";

    const date = new Date(dateStr);

    if (isNaN(date.getTime())) {
      return dateStr;
    }

    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const getDateParts = (dateStr) => {
    if (!dateStr) {
      return {
        day: "--",
        month: "News",
        year: "",
      };
    }

    const date = new Date(dateStr);

    if (isNaN(date.getTime())) {
      return {
        day: "--",
        month: "News",
        year: "",
      };
    }

    return {
      day: date.toLocaleDateString("en-GB", {
        day: "2-digit",
      }),
      month: date.toLocaleDateString("en-GB", {
        month: "short",
      }),
      year: date.getFullYear(),
    };
  };

  // =========================================
  // CLEAR SEARCH
  // =========================================

  const clearSearch = () => {
    setSearch("");
  };

  // =========================================
  // THEME
  // =========================================

  const rootStyle = {
    "--accent": accent,
  };

  // =========================================
  // ANIMATION
  // =========================================

  const containerVariants = {
    hidden: {
      opacity: 0,
    },

    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 35,
    },

    visible: {
      opacity: 1,
      y: 0,

      transition: {
        duration: 0.55,
        ease: "easeOut",
      },
    },
  };

  // =========================================
  // IMAGE URL
  // =========================================

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;

    if (
      imagePath.startsWith("http://") ||
      imagePath.startsWith("https://")
    ) {
      return imagePath;
    }

    return (
      import.meta.env.VITE_API_SOURCE_URL +
      imagePath
    );
  };

  // =========================================
  // NEWS CARD
  // =========================================

  const NewsCard = ({ item, index }) => {
    const date = getDateParts(item.date);

    const imageUrl = getImageUrl(item.images_path);

    return (
      <motion.article
        variants={itemVariants}
        layout
        whileHover={{
          y: -8,
        }}
        transition={{
          duration: 0.3,
        }}
        className="group relative bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-2xl hover:shadow-green-900/10 transition-all duration-300"
      >
        {/* Image Section */}

        <div className="relative h-56 sm:h-60 overflow-hidden bg-gray-100">

          {imageUrl ? (
            <img
              src={imageUrl}
              alt={item.head_line || "University News"}
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-green-50 to-green-100">
              <ImageIcon
                size={55}
                className="text-green-800/30"
              />
            </div>
          )}

          {/* Image Overlay */}

          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-70 group-hover:opacity-90 transition-opacity duration-300" />

          {/* Top Badge */}

          <div className="absolute top-4 left-4">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-md text-green-800 text-xs font-bold shadow-sm">
              <Sparkles size={13} />

              University News
            </span>
          </div>

          {/* Date Badge */}

          <div className="absolute bottom-4 left-4">
            <div className="flex items-center gap-3">
              <div className="flex flex-col items-center justify-center w-14 h-16 rounded-xl bg-white shadow-lg overflow-hidden">
                <span
                  className="w-full text-center text-[10px] font-bold uppercase py-1 text-white"
                  style={{
                    backgroundColor: accent,
                  }}
                >
                  {date.month}
                </span>

                <span className="text-xl font-extrabold text-gray-900">
                  {date.day}
                </span>
              </div>

              <div className="text-white">
                <p className="text-xs text-white/80">
                  Published
                </p>

                <p className="font-semibold text-sm">
                  {date.year}
                </p>
              </div>
            </div>
          </div>

          {/* Image Index */}

          <div className="absolute top-4 right-4">
            <span className="flex items-center justify-center w-9 h-9 rounded-full bg-black/20 backdrop-blur-md border border-white/20 text-white text-xs font-semibold">
              {String(index + 1).padStart(2, "0")}
            </span>
          </div>
        </div>

        {/* Content Section */}

        <div className="p-5 md:p-6">

          {/* Date */}

          <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
            <CalendarDays
              size={14}
              style={{
                color: accent,
              }}
            />

            {formatDate(item.date)}
          </div>

          {/* Title */}

          <h3 className="text-lg font-bold text-gray-800 leading-relaxed line-clamp-3 min-h-[5rem] group-hover:text-green-800 transition-colors duration-300">
            {item.head_line || "Untitled News"}
          </h3>

          {/* Divider */}

          <div className="my-5 h-px bg-gray-100 group-hover:bg-green-100 transition-colors" />

          {/* Read More */}

          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-400">
              Read full story
            </span>

            <Link
              to={`/news/${item.id}`}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-all duration-300 hover:shadow-lg hover:gap-3"
              style={{
                backgroundColor: accent,
              }}
            >
              Read More

              <ArrowRight size={16} />
            </Link>
          </div>
        </div>

        {/* Bottom Accent */}

        <div
          className="absolute bottom-0 left-0 w-0 h-1 group-hover:w-full transition-all duration-500"
          style={{
            backgroundColor: accent,
          }}
        />
      </motion.article>
    );
  };

  // =========================================
  // MAIN RENDER
  // =========================================

  return (
    <div
      className="min-h-screen bg-[#f7faf8] overflow-hidden"
      style={rootStyle}
    >

      {/* =========================================
          HERO SECTION
      ========================================= */}

      <section
        className="relative isolate overflow-hidden"
        style={{
          background: `
            linear-gradient(
              135deg,
              ${accent} 0%,
              #0b4027 50%,
              #062c1b 100%
            )
          `,
        }}
      >
        {/* Decorative Background */}

        <div className="absolute inset-0 overflow-hidden pointer-events-none">

          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 45, 0],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full border border-white/10"
          />

          <motion.div
            animate={{
              x: [0, 40, 0],
              y: [0, 20, 0],
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-white/[0.04] blur-3xl"
          />

          <div className="absolute inset-0 opacity-[0.04]">
            <div
              className="w-full h-full"
              style={{
                backgroundImage:
                  "radial-gradient(#ffffff 1px, transparent 1px)",
                backgroundSize: "28px 28px",
              }}
            />
          </div>
        </div>

        {/* Hero Content */}

        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-16 pt-20 pb-32 md:pt-28 md:pb-40">

          <motion.div
            initial={{
              opacity: 0,
              y: 40,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.8,
              ease: "easeOut",
            }}
            className="flex flex-col items-center text-center"
          >

            {/* University Logo */}

            <motion.div
              initial={{
                scale: 0.6,
                opacity: 0,
              }}
              animate={{
                scale: 1,
                opacity: 1,
              }}
              transition={{
                duration: 0.7,
                delay: 0.2,
                type: "spring",
                stiffness: 120,
              }}
              className="relative mb-8"
            >
              <div className="absolute -inset-4 rounded-full bg-white/10 blur-xl" />

              <div className="relative w-28 h-28 md:w-32 md:h-32 rounded-full bg-white p-3 shadow-2xl ring-4 ring-white/20">
                <img
                  src={logoSrc}
                  alt="Bandarban University Logo"
                  className="w-full h-full object-contain"
                />
              </div>

              <motion.div
                animate={{
                  rotate: 360,
                }}
                transition={{
                  duration: 15,
                  repeat: Infinity,
                  ease: "linear",
                }}
                className="absolute -inset-3 rounded-full border border-dashed border-white/30"
              />
            </motion.div>

            {/* Badge */}

            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-md text-white/90 text-sm font-medium mb-6">
              <Sparkles
                size={16}
                className="text-yellow-300"
              />

              News & Events
            </div>

            {/* Heading */}

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white tracking-tight leading-tight">
              University{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-200 via-white to-green-300">
                News
              </span>
            </h1>

            <p className="mt-6 max-w-2xl text-base md:text-lg text-green-50/80 leading-relaxed">
              Discover the latest happenings, achievements,
              academic activities, and memorable moments
              from Bandarban University.
            </p>

            {/* Hero Statistics */}

            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">

              <div className="flex items-center gap-3 px-5 py-3 rounded-2xl bg-white/10 border border-white/15 backdrop-blur-md">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <Newspaper
                    size={20}
                    className="text-green-200"
                  />
                </div>

                <div className="text-left">
                  <p className="text-white font-bold text-xl">
                    {news.length}
                  </p>

                  <p className="text-green-100/70 text-xs">
                    Total News
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 px-5 py-3 rounded-2xl bg-white/10 border border-white/15 backdrop-blur-md">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                  <TrendingUp
                    size={20}
                    className="text-green-200"
                  />
                </div>

                <div className="text-left">
                  <p className="text-white font-bold text-xl">
                    Latest
                  </p>

                  <p className="text-green-100/70 text-xs">
                    Updates
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Wave */}

        <div className="absolute bottom-0 left-0 w-full leading-none">
          <svg
            viewBox="0 0 1440 120"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-16 md:h-24"
            preserveAspectRatio="none"
          >
            <path
              d="M0,64 C240,120 480,0 720,48 C960,96 1200,24 1440,64 L1440,120 L0,120 Z"
              fill="#f7faf8"
            />
          </svg>
        </div>
      </section>

      {/* =========================================
          NEWS SECTION
      ========================================= */}

      <section className="relative py-12 md:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">

          {/* Section Header */}

          <motion.div
            initial={{
              opacity: 0,
              y: 25,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.3,
            }}
            transition={{
              duration: 0.6,
            }}
            className="text-center mb-10"
          >
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-4"
              style={{
                backgroundColor: `${accent}12`,
                color: accent,
              }}
            >
              <Newspaper size={16} />

              Latest Updates
            </div>

            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900">
              News & Stories
            </h2>

            <p className="mt-3 text-gray-500 max-w-xl mx-auto">
              Explore the latest news, events, and stories
              from our university community.
            </p>
          </motion.div>

          {/* =========================================
              SEARCH PANEL
          ========================================= */}

          <motion.div
            initial={{
              opacity: 0,
              y: 25,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.6,
            }}
            className="bg-white rounded-3xl border border-gray-100 shadow-[0_15px_50px_rgba(0,0,0,0.04)] p-5 md:p-7 mb-10"
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">

              {/* Search Heading */}

              <div className="flex items-center gap-4">
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center"
                  style={{
                    backgroundColor: `${accent}12`,
                    color: accent,
                  }}
                >
                  <Search size={23} />
                </div>

                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    Explore News
                  </h3>

                  <p className="text-sm text-gray-500">
                    Search by news title
                  </p>
                </div>
              </div>

              {/* Search Input */}

              <div className="relative w-full md:max-w-xl">
                <Search
                  size={20}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />

                <input
                  type="text"
                  placeholder="Search news by title..."
                  value={search}
                  onChange={(e) =>
                    setSearch(e.target.value)
                  }
                  className="w-full pl-12 pr-12 py-4 rounded-xl border border-gray-200 bg-gray-50 text-gray-700 outline-none transition-all focus:bg-white focus:ring-4 focus:ring-green-100 focus:border-green-600"
                />

                {search && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 transition"
                    aria-label="Clear search"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
            </div>

            {/* Search Result Information */}

            <div className="flex flex-wrap items-center justify-between gap-3 mt-6 pt-5 border-t border-gray-100">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{
                    backgroundColor: accent,
                  }}
                />

                Showing{" "}
                <span className="font-bold text-gray-900">
                  {filteredNews.length}
                </span>{" "}
                of{" "}
                <span className="font-bold text-gray-900">
                  {Math.min(news.length, 50)}
                </span>{" "}
                news
              </div>

              {search && (
                <button
                  onClick={clearSearch}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-red-500 hover:text-red-700 transition"
                >
                  <X size={15} />

                  Clear Search
                </button>
              )}
            </div>
          </motion.div>

          {/* =========================================
              LOADING SKELETON
          ========================================= */}

          {loading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
                <div
                  key={item}
                  className="bg-white rounded-3xl overflow-hidden border border-gray-100 animate-pulse"
                >
                  <div className="h-56 bg-gray-200" />

                  <div className="p-5 space-y-4">
                    <div className="h-3 w-1/3 bg-gray-200 rounded" />

                    <div className="h-5 w-full bg-gray-200 rounded" />

                    <div className="h-5 w-3/4 bg-gray-200 rounded" />

                    <div className="h-px bg-gray-100" />

                    <div className="h-10 w-32 bg-gray-200 rounded-xl ml-auto" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* =========================================
              ERROR STATE
          ========================================= */}

          {!loading && error && (
            <motion.div
              initial={{
                opacity: 0,
                scale: 0.95,
              }}
              animate={{
                opacity: 1,
                scale: 1,
              }}
              className="bg-white rounded-3xl border border-red-100 p-10 text-center shadow-sm"
            >
              <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-5">
                <RefreshCw
                  size={28}
                  className="text-red-500"
                />
              </div>

              <h3 className="text-xl font-bold text-gray-900">
                Unable to Load News
              </h3>

              <p className="text-gray-500 mt-2 mb-6">
                {error}
              </p>

              <button
                onClick={newsPost}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-semibold transition hover:opacity-90"
                style={{
                  backgroundColor: accent,
                }}
              >
                <RefreshCw size={17} />

                Try Again
              </button>
            </motion.div>
          )}

          {/* =========================================
              NEWS GRID
          ========================================= */}

          {!loading &&
            !error &&
            filteredNews.length > 0 && (
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
              >
                {filteredNews.map((item, index) => (
                  <NewsCard
                    key={item.id || index}
                    item={item}
                    index={index}
                  />
                ))}
              </motion.div>
            )}

          {/* =========================================
              EMPTY STATE
          ========================================= */}

          {!loading &&
            !error &&
            filteredNews.length === 0 && (
              <AnimatePresence>
                <motion.div
                  initial={{
                    opacity: 0,
                    y: 20,
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                  }}
                  exit={{
                    opacity: 0,
                  }}
                  className="bg-white rounded-3xl border border-gray-100 shadow-sm py-16 px-6 text-center"
                >
                  <motion.div
                    animate={{
                      y: [0, -8, 0],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    className="w-24 h-24 rounded-3xl bg-green-50 flex items-center justify-center mx-auto mb-6"
                  >
                    <Newspaper
                      size={42}
                      className="text-green-700"
                    />
                  </motion.div>

                  <h3 className="text-2xl font-bold text-gray-900">
                    No News Found
                  </h3>

                  <p className="text-gray-500 mt-3 max-w-md mx-auto">
                    {search
                      ? "We couldn't find any news matching your search. Try another keyword."
                      : "There is currently no news available. Please check back later."}
                  </p>

                  {search && (
                    <button
                      onClick={clearSearch}
                      className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white transition hover:opacity-90"
                      style={{
                        backgroundColor: accent,
                      }}
                    >
                      <RefreshCw size={16} />

                      Clear Search
                    </button>
                  )}
                </motion.div>
              </AnimatePresence>
            )}

          {/* =========================================
              BOTTOM INFORMATION
          ========================================= */}

          {!loading && !error && news.length > 0 && (
            <motion.div
              initial={{
                opacity: 0,
              }}
              whileInView={{
                opacity: 1,
              }}
              viewport={{
                once: true,
              }}
              className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4 p-5 rounded-2xl bg-white border border-gray-100"
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{
                    backgroundColor: `${accent}12`,
                    color: accent,
                  }}
                >
                  <Newspaper size={19} />
                </div>

                <div>
                  <p className="font-semibold text-gray-800 text-sm">
                    Stay Connected
                  </p>

                  <p className="text-xs text-gray-500 mt-1">
                    Discover what's happening at our university.
                  </p>
                </div>
              </div>

              <div
                className="text-sm font-semibold flex items-center gap-2"
                style={{
                  color: accent,
                }}
              >
                Bandarban University

                <ChevronRight size={16} />
              </div>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
}

// =========================================
// PROP TYPES
// =========================================

NewsList.propTypes = {
  logoSrc: PropTypes.string,
  accent: PropTypes.string,
  pageTitle: PropTypes.string,
};